import type {
  FormContent,
  Question,
  QuestionGroup,
  RepeatField,
} from "./types";
import { byPos } from "./sortKey";

/** The same ordinary questions are used in and outside groups. */
export function groupMembers(
  form: FormContent,
  group: QuestionGroup,
): Question[] {
  return form.questions
    .filter((q) => q.groupId === group.id && q.sectionId === group.sectionId)
    .sort(byPos);
}

/** Resolve authored repeat groups into the existing response pipeline without changing stored definitions. */
export function questionsForAnswers(form: FormContent): Question[] {
  const repeats = new Map(
    (form.groups ?? []).filter((g) => g.repeat).map((g) => [g.id, g]),
  );

  if (!repeats.size) {
    return form.questions;
  }

  const emitted = new Set<string>();
  return [...form.questions].sort(byPos).flatMap((question): Question[] => {
    const group = question.groupId ? repeats.get(question.groupId) : undefined;

    if (!group || group.sectionId !== question.sectionId) {
      return [question];
    }

    if (emitted.has(group.id)) {
      return [];
    }

    emitted.add(group.id);
    const members = groupMembers(form, group);
    return [
      {
        id: group.id,
        sectionId: group.sectionId,
        label: group.title,
        help: group.description,
        pos: members[0]?.pos ?? question.pos,
        type: "repeatable_group",
        required: group.repeat!.minEntries > 0,
        config: {
          ...group.repeat,
          fields: members
            .filter((q) => q.type !== "repeatable_group")
            .map(
              ({ sectionId: _section, groupId: _group, ...field }) =>
                field as RepeatField,
            ),
        },
      },
    ];
  });
}

export type QuestionBlock<T = Question> = {
  key: string;
  group?: QuestionGroup;
  questions: T[];
};

/** Contiguous runs keep malformed collaborative drafts visible instead of silently losing questions. */
export function questionBlocks<T extends { id: string; groupId?: string }>(
  form: Pick<FormContent, "groups">,
  questions: readonly T[],
): QuestionBlock<T>[] {
  const groups = new Map(
    (form.groups ?? []).filter((g) => !g.repeat).map((g) => [g.id, g]),
  );
  const result: QuestionBlock<T>[] = [];

  for (const question of questions) {
    const group = question.groupId ? groups.get(question.groupId) : undefined;
    const previous = result.at(-1);

    if (group && previous?.group?.id === group.id) {
      previous.questions.push(question);
    } else {
      result.push({
        key: JSON.stringify([group?.id ?? null, question.id]),
        group,
        questions: [question],
      });
    }
  }

  return result;
}

/** A concurrent deletion can leave a question pointing to a missing container. */
export function orphanedQuestions(form: FormContent): Question[] {
  const sectionIds = new Set(form.sections.map((section) => section.id));
  return form.questions.filter(
    (question) =>
      !sectionIds.has(question.sectionId) ||
      (!!question.groupId &&
        !form.groups?.some(
          (group) =>
            group.id === question.groupId &&
            group.sectionId === question.sectionId,
        )),
  );
}
